import React, { useEffect, useState } from 'react';
import { CustomFieldScope, CustomFieldType, SaveCustomFieldValueDTO } from '../../types/custom-field';
import { useCustomFields } from '../../hooks/useCustomFields';
import CustomFieldRow from './CustomFieldRow';
import AddCustomFieldModal from './AddCustomFieldModal';
import AIExtractionConfigPopover from './AIExtractionConfigPopover';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Plus } from '@phosphor-icons/react';
import { toast } from 'react-toastify';

interface CustomFieldsSectionProps {
  scope: 'building' | 'unit';
  entityId: string;
  buildingId?: string;
  isDisabled?: boolean;
}

const CustomFieldsSection: React.FC<CustomFieldsSectionProps> = ({
  scope,
  entityId,
  buildingId,
  isDisabled = false,
}) => {
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [aiConfigFieldId, setAiConfigFieldId] = useState<number | null>(null);

  const fieldScope = scope === 'building' ? CustomFieldScope.BUILDING : CustomFieldScope.UNIT;

  const {
    definitions,
    values,
    isLoading,
    fetchDefinitions,
    fetchValues,
    saveValue,
    createDefinition,
    deleteDefinition,
    updateAIConfig,
  } = useCustomFields();

  useEffect(() => {
    const loadData = async () => {
      try {
        await fetchDefinitions(fieldScope);
        await fetchValues(fieldScope, entityId, buildingId);
      } catch (error) {
        // Error is handled in the hook
      }
    };

    loadData();
  }, [fieldScope, entityId, buildingId, fetchDefinitions, fetchValues]);

  const handleSaveValue = async (fieldDefinitionId: number, value: string | number | boolean | null) => {
    try {
      const dto: SaveCustomFieldValueDTO = {
        fieldDefinitionId,
      };

      // Set the appropriate entity ID
      if (fieldScope === CustomFieldScope.BUILDING) {
        dto.buildingId = entityId;
      } else {
        dto.unitId = entityId;
      }

      // Set the appropriate value field based on the field type
      const definition = definitions.find((def) => def.id === fieldDefinitionId);
      if (!definition) {
        toast.error('Veld definitie niet gevonden');
        return;
      }

      switch (definition.fieldType) {
        case CustomFieldType.TEXT:
        case CustomFieldType.DROPDOWN:
          dto.textValue = value as string;
          break;
        case CustomFieldType.NUMBER:
          dto.numberValue = value as number;
          break;
        case CustomFieldType.BOOLEAN:
          dto.booleanValue = value as boolean;
          break;
      }

      await saveValue(dto);
    } catch (error) {
      // Error is handled in the hook
    }
  };

  const handleAddField = async (
    name: string,
    fieldType: CustomFieldType,
    dropdownOptions?: string[]
  ) => {
    try {
      await createDefinition({
        name,
        fieldType,
        scope: fieldScope,
        dropdownOptions,
      });
    } catch (error) {
      // Error is handled in the hook
      throw error;
    }
  };

  const handleDeleteField = async (fieldDefinitionId: number) => {
    const definition = definitions.find((def) => def.id === fieldDefinitionId);
    if (!definition) return;

    const confirmed = window.confirm(
      `Weet je zeker dat je het veld "${definition.name}" wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.`
    );

    if (confirmed) {
      try {
        await deleteDefinition(fieldDefinitionId);
      } catch (error) {
        // Error is handled in the hook
      }
    }
  };

  const handleConfigureAI = (fieldDefinitionId: number) => {
    setAiConfigFieldId(fieldDefinitionId);
  };

  const handleSaveAIConfig = async (config: any) => {
    if (aiConfigFieldId === null) return;

    try {
      await updateAIConfig(aiConfigFieldId, config);
      setAiConfigFieldId(null);
    } catch (error) {
      // Error is handled in the hook
      throw error;
    }
  };

  const activeDefinitions = definitions.filter((def) => def.active);

  const currentAIConfigDefinition = aiConfigFieldId
    ? definitions.find((def) => def.id === aiConfigFieldId)
    : null;

  return (
    <Card data-tour="custom-fields-section">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <div>
          <CardTitle className="text-lg">Aangepaste velden</CardTitle>
          <CardDescription>
            Extra informatie voor {scope === 'building' ? 'dit gebouw' : 'deze unit'}
          </CardDescription>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setAddModalOpen(true)}
          disabled={isDisabled || isLoading}
          data-tour="custom-fields-add"
        >
          <Plus size={16} className="mr-2" />
          Veld toevoegen
        </Button>
      </CardHeader>

      <CardContent>
        {isLoading && definitions.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4">Laden...</div>
        ) : activeDefinitions.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4">
            Geen aangepaste velden geconfigureerd. Klik op "Veld toevoegen" om te beginnen.
          </div>
        ) : (
          <div className="space-y-1">
            {activeDefinitions.map((definition) => {
              const value = values.find((v) => v.fieldDefinitionId === definition.id);
              return (
                <CustomFieldRow
                  key={definition.id}
                  definition={definition}
                  value={value}
                  onSave={handleSaveValue}
                  onDelete={handleDeleteField}
                  onConfigureAI={handleConfigureAI}
                  isDisabled={isDisabled}
                />
              );
            })}
          </div>
        )}
      </CardContent>

      <AddCustomFieldModal
        open={addModalOpen}
        onOpenChange={setAddModalOpen}
        scope={fieldScope}
        onAdd={handleAddField}
      />

      {currentAIConfigDefinition && (
        <AIExtractionConfigPopover
          trigger={<div />}
          currentConfig={currentAIConfigDefinition.aiExtractionConfig}
          onSave={handleSaveAIConfig}
          isDisabled={isDisabled}
        />
      )}
    </Card>
  );
};

export default CustomFieldsSection;
